import type { CSSProperties } from "react";
import {
  color,
  OVERLAY_OPACITY,
  type Overlay,
  type Pattern,
  type Radius,
  type SectionSpace,
  type Shadow,
  type Space,
} from "@/app/catalog/tokens";

// The CSS of the design tokens catalog props choose from
// (app/catalog/tokens.ts), and the classes the catalog's components share.

export function space(token: Space | undefined) {
  return token && `var(--space-${token})`;
}

export function sectionSpace(token: SectionSpace | undefined) {
  return token && `var(--section-space-${token})`;
}

// Relative to the theme's --radius, and computed where it is used so that a
// page theme's --radius applies.
const RADII: Record<Radius, string> = {
  none: "0",
  sm: "calc(var(--radius) - 4px)",
  md: "var(--radius)",
  lg: "calc(var(--radius) + 4px)",
  xl: "calc(var(--radius) + 12px)",
  full: "9999px",
};

// The corners of buttons and single-line fields: the theme's radius, or pills
// in a theme whose radius is "full".
export const CONTROL_RADIUS = "rounded-[var(--radius-control)]";

// An outline button on a page: see-through, its text and border in the colour
// around it, so it reads on whatever band or picture it sits on.
export const OUTLINE_CONTROL =
  "bg-transparent border-[color:color-mix(in_oklch,currentColor_35%,transparent)]";

export function radius(token: Radius | undefined) {
  return token && RADII[token];
}

export function shadow(token: Shadow | undefined) {
  return token && `var(--shadow-${token})`;
}

// Secondary text (a Meta's label, a Counter, a caption) when it has no colour
// of its own: its ground's MUTED_INK, else the theme's muted-foreground.
export const MUTED_INK = "var(--muted-ink, hsl(var(--muted-foreground)))";
// A field's placeholder, in the same colour.
export const MUTED_PLACEHOLDER =
  "placeholder:text-[color:var(--muted-ink,hsl(var(--muted-foreground)))]";

// The secondary text's colour on a ground whose ink is `text`: the theme's
// muted-foreground under the page's text, the ink faded under any other.
export function mutedInk(text: string) {
  return text === color("foreground")
    ? color("muted-foreground")!
    : `color-mix(in oklch, ${text} 70%, transparent)`;
}

// The style of an element that paints a ground whose ink is `text`: its text
// colour, which what it holds inherits, and its secondary text's.
export function inkStyle(text: string | undefined): CSSProperties {
  if (!text) return {};
  return { color: text, "--muted-ink": mutedInk(text) } as CSSProperties;
}

// A background pattern drawn in `lineColor` over the element's background.
export function pattern(token: Pattern | undefined, lineColor: string) {
  if (!token || token === "none") return {};
  const line = color(lineColor);
  return token === "grid"
    ? {
        backgroundImage: `linear-gradient(to right, ${line} 1px, transparent 1px), linear-gradient(to bottom, ${line} 1px, transparent 1px)`,
        backgroundSize: "24px 24px",
      }
    : {
        backgroundImage: `radial-gradient(${line} 1.5px, transparent 1.5px)`,
        backgroundSize: "16px 16px",
      };
}

// Every size-like token by its place on a size scale, smallest first, with
// the words the design panel shows for it. Synonyms share a rank: a "default"
// size sits where "md" does (a Button's sizes are default, sm and lg).
const SIZE_STEPS: Record<string, { rank: number; label: string }> = {
  none: { rank: 0, label: "None" },
  xs: { rank: 1, label: "Extra small" },
  // A Cover's overlay (`OVERLAY_OPACITY`) between none, medium and full.
  faint: { rank: 1, label: "Faint" },
  light: { rank: 2, label: "Light" },
  strong: { rank: 4, label: "Strong" },
  heavy: { rank: 5, label: "Heavy" },
  solid: { rank: 6, label: "Solid" },
  sm: { rank: 2, label: "Small" },
  small: { rank: 2, label: "Small" },
  md: { rank: 3, label: "Medium" },
  medium: { rank: 3, label: "Medium" },
  default: { rank: 3, label: "Default" },
  base: { rank: 3, label: "Base" },
  lg: { rank: 4, label: "Large" },
  large: { rank: 4, label: "Large" },
  xl: { rank: 5, label: "Extra large" },
  "2xl": { rank: 6, label: "2XL" },
  "3xl": { rank: 7, label: "3XL" },
  "4xl": { rank: 8, label: "4XL" },
  "5xl": { rank: 9, label: "5XL" },
  "6xl": { rank: 10, label: "6XL" },
  "7xl": { rank: 11, label: "7XL" },
  "8xl": { rank: 12, label: "8XL" },
  screen: { rank: 13, label: "Screen" },
  full: { rank: 13, label: "Full" },
};

// An enum's options as a size scale, smallest first, when every option is a
// distinct size token: the design panel edits it with a stepped slider rather
// than a select, whatever order the schema lists them in. Null otherwise.
export function sizeScale(options: readonly (string | number)[]): readonly string[] | null {
  if (options.length < 3 || !options.every((option) => typeof option === "string")) return null;
  const steps = options as readonly string[];
  if (!steps.every((step) => Object.hasOwn(SIZE_STEPS, step))) return null;
  const ranks = new Set(steps.map((step) => SIZE_STEPS[step]!.rank));
  if (ranks.size !== steps.length) return null;
  return steps.toSorted((a, b) => SIZE_STEPS[a]!.rank - SIZE_STEPS[b]!.rank);
}

// A size step in words ("sm" → "Small"), or a step of an overlay's scale as
// its opacity ("light" → "25%").
export function sizeLabel(step: string, steps: readonly string[] = []) {
  if (steps.length > 0 && steps.every((each) => Object.hasOwn(OVERLAY_OPACITY, each))) {
    return `${OVERLAY_OPACITY[step as Overlay]}%`;
  }
  return Object.hasOwn(SIZE_STEPS, step) ? SIZE_STEPS[step]!.label : step;
}
